export type LoginMethod = 'otp' | 'password' | 'passkey';

export const LAST_LOGIN_METHOD_KEY = 'aegis:last-login-method';

const DEFAULT_ORDER: LoginMethod[] = ['otp', 'passkey', 'password'];

function isLoginMethod(value: string | null): value is LoginMethod {
  return value === 'otp' || value === 'password' || value === 'passkey';
}

/** 로그인 성공에 사용한 방법을 브라우저에 기억한다. */
export function saveLastLoginMethod(method: LoginMethod): void {
  try {
    localStorage.setItem(LAST_LOGIN_METHOD_KEY, method);
  } catch {
    // 스토리지 미지원·용량 초과는 무시 — 정렬만 기본값으로 돌아간다
  }
}

/** 기억된 로그인 방법. 없거나 알 수 없는 값이면 null. */
export function loadLastLoginMethod(): LoginMethod | null {
  try {
    const stored = localStorage.getItem(LAST_LOGIN_METHOD_KEY);
    return isLoginMethod(stored) ? stored : null;
  } catch {
    return null;
  }
}

export interface OrderLoginMethodsOptions {
  lastMethod: LoginMethod | null;
  hasPasskey: boolean;
  supportsPasskey: boolean;
}

/**
 * 방법 선택 스텝의 버튼 순서. 첫 항목이 primary 다.
 * 패스키가 불가하면(미등록·브라우저 미지원) 목록에서 빠지고,
 * 기억된 방법이 그 패스키였다면 자연히 기본 정렬로 폴백한다.
 */
export function orderLoginMethods({
  lastMethod,
  hasPasskey,
  supportsPasskey,
}: OrderLoginMethodsOptions): LoginMethod[] {
  const passkeyAvailable = hasPasskey && supportsPasskey;
  const available = DEFAULT_ORDER.filter((m) => m !== 'passkey' || passkeyAvailable);

  if (!lastMethod || !available.includes(lastMethod)) return available;

  return [lastMethod, ...available.filter((m) => m !== lastMethod)];
}

/**
 * 방법 선택 화면을 건너뛰고 곧장 실행할 방법. 없으면 null — 선택 화면을 보여준다.
 * 비밀번호는 어차피 입력 화면을 거치므로 건너뛸 것이 없다.
 */
export function pickAutoLoginMethod(
  options: OrderLoginMethodsOptions
): Exclude<LoginMethod, 'password'> | null {
  const [first] = orderLoginMethods(options);
  if (options.lastMethod !== first) return null;
  return first === 'otp' || first === 'passkey' ? first : null;
}

export const LAST_LOGIN_ACCOUNT_KEY = 'aegis:last-login-account';

/** 로그인에 성공한 계정의 accountKey. 동명이인 후보 목록에서 맨 위에 세우는 데만 쓴다. */
export function saveLastLoginAccount(accountKey: string): void {
  try {
    localStorage.setItem(LAST_LOGIN_ACCOUNT_KEY, accountKey);
  } catch {
    // 스토리지 미지원·용량 초과는 무시 — 후보가 서버 순서로 보일 뿐이다
  }
}

export function loadLastLoginAccount(): string | null {
  try {
    return localStorage.getItem(LAST_LOGIN_ACCOUNT_KEY);
  } catch {
    return null;
  }
}

/** 지난번 계정을 맨 위로. 자동으로 고르지는 않는다 — 같은 PC 의 다른 동명이인에게 코드가 가지 않게. */
export function orderCandidatesByLastAccount<T extends { accountKey: string }>(
  candidates: T[],
  lastAccountKey: string | null
): T[] {
  const last = candidates.find((c) => c.accountKey === lastAccountKey);
  if (!last) return candidates;
  return [last, ...candidates.filter((c) => c !== last)];
}
